import { useState, useEffect, useCallback } from 'react';
import { useMediaStore } from '../stores/mediaStore';

export const useLocalMediaPreview = () => {
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { audioDeviceId, videoDeviceId } = useMediaStore();

  const startPreview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Проверка доступности API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          'WebRTC не поддерживается. Используйте HTTPS или localhost для доступа к камере и микрофону.'
        );
      }

      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId
          ? { deviceId: { exact: audioDeviceId } }
          : true,
        video: videoDeviceId
          ? {
              deviceId: { exact: videoDeviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            }
          : {
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setPreviewStream(stream);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to get media preview:', err);
      setError(err.message || 'Failed to access media devices');
      setLoading(false);
    }
  }, [audioDeviceId, videoDeviceId]);

  const stopPreview = useCallback(() => {
    if (previewStream) {
      previewStream.getTracks().forEach((track) => track.stop());
      setPreviewStream(null);
    }
  }, [previewStream]);

  useEffect(() => {
    startPreview();

    return () => {
      stopPreview();
    };
  }, [audioDeviceId, videoDeviceId]);

  return {
    previewStream,
    loading,
    error,
    startPreview,
    stopPreview,
  };
};
